// components/LandingPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pageVariants, buttonVariants } from '../utils/animationUtils';

const LandingPage = ({ onStartTrial }) => {
    return (
        <motion.div
            className="relative min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center p-8 overflow-hidden"
            variants={pageVariants}
            initial="initial"
            animate="in"
            exit="out"
        >
            <div className="relative w-full max-w-3xl">
                {/* 히어로 섹션 - 서비스 소개 */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-10 mb-6">
                    <div className="flex items-start justify-between gap-10 flex-col md:flex-row">
                        <div className="flex-1">
                            <h1 className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-gray-900 leading-[1.1] mb-4">
                                영업이 쉬워지는 순간<br />
                                FC퍼널
                            </h1>
                            <p className="text-gray-700 text-base md:text-lg leading-relaxed max-w-xl">
                                FC퍼널은 200여명의 MDRT, COT 회원들과의 성공사례 인터뷰를 집약해 탄생한 고객관리 시스템입니다.
                            </p>
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/70">
                                    <div className="font-semibold text-gray-900 text-sm">체계적인 고객/영업관리</div>
                                    <div className="text-xs text-gray-600 mt-1">단계별 퍼널과 메모·히스토리로 일의 흐름이 선명해집니다.</div>
                                </div>
                                <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/70">
                                    <div className="font-semibold text-gray-900 text-sm">평생 업데이트</div>
                                    <div className="text-xs text-gray-600 mt-1">사용자 피드백을 반영해 지속적으로 개선하고 성장합니다.</div>
                                </div>
                            </div>
                        </div>
                        <div className="w-full md:w-64">
                            <div className="bg-white border border-gray-200 rounded-2xl p-5 transition-transform duration-300 hover:-translate-y-0.5">
                                <div className="font-bold text-gray-900 mb-2">바로 체험해보세요</div>
                                <p className="text-sm text-gray-600 mb-4">회원가입 없이 시작하고, 마음에 들면 계속 사용하세요.</p>

                                <motion.button
                                    onClick={onStartTrial}
                                    className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl hover:bg-black text-sm font-bold"
                                    variants={buttonVariants}
                                    whileHover="hover"
                                    whileTap="tap"
                                >
                                    7일 무료 체험 시작
                                    <ChevronRight className="w-4 h-4" />
                                </motion.button>

                                {/* 안내 문구 제거 유지 */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 이용자 후기 섹션 */}
                <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl shadow-xl p-6">
                    <div className="mb-4">
                        <div className="text-[11px] font-bold text-indigo-600 tracking-widest">USER STORIES</div>
                        <h2 className="text-2xl font-extrabold text-gray-900 mt-1">이용자들의 솔직한 이야기</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-xl border border-indigo-100 bg-white">
                            <p className="text-gray-800 text-sm leading-relaxed">“주먹구구식으로 영업했던 그동안의 시간이 아까울 따름입니다.”</p>
                            <div className="mt-3 text-xs text-gray-500 text-right">굿리치 김OO팀장님</div>
                        </div>
                        <div className="p-4 rounded-xl border border-emerald-100 bg-white">
                            <p className="text-gray-800 text-sm leading-relaxed">“늘상 내일은 누구를 만나야 하나, 어디에 전화를 걸어 약속을 잡나 고민을 했던 저에게 정말 보석같은 앱입니다.”</p>
                            <div className="mt-3 text-xs text-gray-500 text-right">인카금융 박OOFA님</div>
                        </div>
                        <div className="p-4 rounded-xl border border-amber-100 bg-white">
                            <p className="text-gray-800 text-sm leading-relaxed">“이 비용으로 이 정도의 효율을 낼 수 있다는거 자체가 놀랍습니다.”</p>
                            <div className="mt-3 text-xs text-gray-500 text-right">한화금융 이OOFC님</div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Footer: Powered by 회사 로고 */}
            <motion.div
                className="absolute bottom-4 left-0 right-0 flex items-center justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <motion.a
                    href="https://previaworks.netlify.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-200 shadow-sm hover:bg-white/90 transition"
                    aria-label="Visit Previaworks website"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <span className="text-xs font-medium text-gray-600">Powered by</span>
                    <img
                        src="/images/logo(previaworks)_1300x500.png"
                        alt="회사 로고"
                        className="h-6 w-auto object-contain"
                    />
                </motion.a>
                <span className="mx-2 text-gray-300">·</span>
                <Link to="/terms" className="text-xs text-gray-500 hover:text-gray-800 underline">이용약관</Link>
                <span className="mx-2 text-gray-300">·</span>
                <Link to="/privacy" className="text-xs text-gray-500 hover:text-gray-800 underline">개인정보처리방침</Link>
            </motion.div>
        </motion.div>
    );
};

export default LandingPage;
